import { cn } from '@/lib/utils';
import { useEffect, useState, useRef } from 'react';

interface LogoProps {
  className?: string;
}

const words = ['Job', 'Hire', 'Skill', 'Connect', 'Comp', 'Business'];
const ANIMATION_DURATION = 300;
const WORD_DISPLAY_DURATION = 2000;
const CYCLE_PAUSE_DURATION = 10000;

export function Logo({ className }: LogoProps) {
  const [currentWord, setCurrentWord] = useState('Loop');
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const wordIndexRef = useRef(0);

  useEffect(() => {
    const startCycle = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      const cycleWords = () => {
        setIsAnimating(true);
        
        timeoutRef.current = setTimeout(() => {
          const nextWord = words[wordIndexRef.current];
          setCurrentWord(nextWord);
          setIsAnimating(false);
          
          wordIndexRef.current = (wordIndexRef.current + 1) % words.length;
          
          // If we've shown all words, show Loop and pause
          if (wordIndexRef.current === 0) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
            }
            
            timeoutRef.current = setTimeout(() => {
              setIsAnimating(true);
              timeoutRef.current = setTimeout(() => {
                setCurrentWord('Loop');
                setIsAnimating(false);
                
                // Wait before starting next cycle
                timeoutRef.current = setTimeout(startCycle, CYCLE_PAUSE_DURATION);
              }, ANIMATION_DURATION);
            }, WORD_DISPLAY_DURATION);
          }
        }, ANIMATION_DURATION);
      };

      // Start the cycle
      cycleWords();
      intervalRef.current = setInterval(cycleWords, WORD_DISPLAY_DURATION + ANIMATION_DURATION);
    };

    // Initial delay before starting first cycle
    timeoutRef.current = setTimeout(startCycle, CYCLE_PAUSE_DURATION);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <div className={cn("font-bold text-xl inline-flex items-center", className)}>
      <svg 
        width="24" 
        height="24" 
        viewBox="0 0 4200 3140" 
        className="text-primary"
        fill="currentColor"
        style={{ 
          transform: 'scale(0.95)',
          position: 'relative',
          top: '1px',
          marginRight: '0.35rem'
        }}
      >
        <path d="M520 1700 l0 -750 477 0 478 0 270 270 270 270 270 -270 270 -270 477 0 478 0 0 750 0 750 -477 0 -478 0 -270 -270 -270 -270 -270 270 -270 270 -477 0 -478 0 0 -750z m1060 225 l225 -225 -225 -225 -225 -225 -270 2 -270 3 -3 435 c-1 239 0 441 3 448 3 9 67 12 272 12 l268 0 225 -225z m1635 -225 l0 -445 -270 -3 -270 -2 -225 225 -225 225 225 225 225 225 270 -2 270 -3 0 -445z" />
      </svg>
      <span className="text-primary font-extrabold tracking-tight">New</span>
      <div className="relative overflow-hidden" style={{ width: '5.5rem' }}>
        <span 
          className={cn(
            "inline-block transition-all duration-300 font-extrabold tracking-tight",
            isAnimating 
              ? "-translate-y-full opacity-0 rotate-x-90"
              : "translate-y-0 opacity-100 rotate-x-0"
          )}
          style={{ transformOrigin: 'bottom' }}
        >
          {currentWord}
        </span>
      </div>
    </div>
  );
}